import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Search, Shield, LogIn, UserX, Users, RefreshCw, ChevronLeft, ChevronRight, Activity, AlertTriangle, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const ACTION_CONFIG = {
  login: { label: "Login", icon: LogIn, color: "bg-emerald-500/10 text-emerald-400" },
  role_change: { label: "Role Change", icon: Shield, color: "bg-amber-500/10 text-amber-400" },
  status_change: { label: "Status Change", icon: RefreshCw, color: "bg-blue-500/10 text-blue-400" },
  user_delete: { label: "User Deleted", icon: UserX, color: "bg-red-500/10 text-red-400" },
  bulk_delete: { label: "Bulk Delete", icon: Users, color: "bg-red-500/10 text-red-400" },
};

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [security, setSecurity] = useState(null);
  const limit = 20;

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (filterAction) params.action = filterAction;
      if (search) params.actor = search;
      const res = await apiClient.get('/admin/audit-log', { params });
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    } finally {
      setLoading(false);
    }
  }, [skip, filterAction, search]);

  const fetchSecurity = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/security-stats');
      setSecurity(res.data);
    } catch (err) {
      console.error('Failed to fetch security stats:', err);
    }
  }, []);

  useEffect(() => { fetchLog(); }, [fetchLog]);
  useEffect(() => { fetchSecurity(); }, [fetchSecurity]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    fetchLog();
  };

  const formatTime = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="admin-audit-log">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-white mb-1">Audit Log</h1>
          <p className="text-white/40 text-sm">{total} recorded events</p>
        </div>

        {/* Security Stats */}
        {security && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="security-stats">
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <LogIn className="w-4 h-4 text-emerald-400" />
                <span className="text-white/40 text-xs uppercase">Logins (24h)</span>
              </div>
              <p className="text-2xl font-semibold text-emerald-400" data-testid="logins-24h">{security.last_24h.success}</p>
            </div>
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-white/40 text-xs uppercase">Failed (24h)</span>
              </div>
              <p className="text-2xl font-semibold text-red-400" data-testid="failed-24h">{security.last_24h.failed}</p>
            </div>
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <LogIn className="w-4 h-4 text-emerald-400" />
                <span className="text-white/40 text-xs uppercase">Logins (7d)</span>
              </div>
              <p className="text-2xl font-semibold text-white" data-testid="logins-7d">{security.last_7d.success}</p>
            </div>
            <div className="bg-[#161821] border border-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-white/40 text-xs uppercase">Failed (7d)</span>
              </div>
              <p className="text-2xl font-semibold text-white" data-testid="failed-7d">{security.last_7d.failed}</p>
            </div>
          </div>
        )}

        {/* Suspicious IPs */}
        {security?.suspicious_ips?.length > 0 && (
          <div className="bg-red-500/5 border border-red-500/10 rounded-2xl p-4" data-testid="suspicious-ips">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-4 h-4 text-red-400" />
              <h3 className="text-red-400 font-medium text-sm">Suspicious Activity (Last 24h)</h3>
            </div>
            <div className="space-y-2">
              {security.suspicious_ips.map((s, i) => (
                <div key={`${s.ip}-${s.last_email}`} className="flex items-center justify-between text-sm">
                  <span className="text-white/60 font-mono text-xs">{s.ip}</span>
                  <span className="text-white/40 text-xs">{s.last_email}</span>
                  <span className="text-red-400 text-xs font-medium">{s.attempts} failed attempts</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              className={`rounded-xl text-xs ${!filterAction ? "bg-amber-500 text-black" : "bg-white/5 text-white/50 hover:text-white"}`}
              onClick={() => { setFilterAction(""); setSkip(0); }}
              data-testid="filter-all"
            >
              All
            </Button>
            {Object.entries(ACTION_CONFIG).map(([key, cfg]) => (
              <Button
                key={key}
                size="sm"
                className={`rounded-xl text-xs ${filterAction === key ? "bg-amber-500 text-black" : "bg-white/5 text-white/50 hover:text-white"}`}
                onClick={() => { setFilterAction(key); setSkip(0); }}
                data-testid={`filter-${key}`}
              >
                {cfg.label}
              </Button>
            ))}
          </div>
          <form onSubmit={handleSearch} className="flex gap-2 ml-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                type="text"
                placeholder="Search by email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-[#161821] border-white/10 text-white placeholder:text-white/30 w-56 rounded-xl text-sm"
                data-testid="audit-search-input"
              />
            </div>
            <Button type="submit" size="sm" className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl" data-testid="audit-search-btn">
              Search
            </Button>
          </form>
        </div>

        {/* Log Table */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            <div className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/30 text-xs uppercase border-b border-white/5">
                      <th className="text-left p-4">Time</th>
                      <th className="text-left p-4">Action</th>
                      <th className="text-left p-4">Actor</th>
                      <th className="text-left p-4">Target</th>
                      <th className="text-left p-4">Details</th>
                      <th className="text-left p-4">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const cfg = ACTION_CONFIG[e.action] || { label: e.action, icon: Activity, color: "bg-white/5 text-white/40" };
                      const Icon = cfg.icon;
                      return (
                        <tr key={e.log_id || `audit-${e.timestamp}-${i}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`audit-row-${i}`}>
                          <td className="p-4 text-white/40 text-xs whitespace-nowrap">{formatTime(e.timestamp)}</td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${cfg.color}`}>
                              <Icon className="w-3 h-3" />
                              {cfg.label}
                            </span>
                          </td>
                          <td className="p-4">
                            <div>
                              <span className="text-white/70 text-xs">{e.actor_email}</span>
                              {e.actor_role && (
                                <span className="ml-1.5 text-[10px] text-amber-400/60 uppercase">{e.actor_role}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-white/50 text-xs">{e.target_email || "—"}</td>
                          <td className="p-4 text-white/40 text-xs max-w-[200px] truncate">{e.details}</td>
                          <td className="p-4 text-white/30 text-xs font-mono">{e.ip || "—"}</td>
                        </tr>
                      );
                    })}
                    {entries.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-white/30">No audit entries found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-white/40 text-sm">Page {currentPage} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0}
                    className="border-white/10 text-white/50 hover:text-white rounded-xl"
                    data-testid="audit-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(skip + limit)}
                    disabled={skip + limit >= total}
                    className="border-white/10 text-white/50 hover:text-white rounded-xl"
                    data-testid="audit-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
